/**
 * TalkingAvatar – shows a face that indicates "speaking" when TTS is playing.
 * Optional: imageUrl (still image), videoUrl (on-demand video), or stream (WebRTC MediaStream, e.g. D-ID).
 */
import React, { useEffect, useRef } from "react";

const DEFAULT_SIZE = 40;

export default function TalkingAvatar({
  isSpeaking = false,
  imageUrl = null,
  videoUrl = null,
  stream = null,
  onVideoEnded = null,
  size = DEFAULT_SIZE,
  className = "",
  fillContainer = false,
}) {
  const videoRef = useRef(null);
  const sizeStyle = fillContainer
    ? { width: "100%", height: "100%", minHeight: 0 }
    : { width: size, height: size };

  // WebRTC stream: attach to video element
  useEffect(() => {
    if (!videoRef.current || !stream) return;
    videoRef.current.srcObject = stream;
    videoRef.current.play().catch(() => {});
    return () => {
      if (videoRef.current) videoRef.current.srcObject = null;
    };
  }, [stream]);

  if (imageUrl) {
    return (
      <div
        className={className}
        style={{ ...sizeStyle, borderRadius: fillContainer ? "0.5rem" : "50%", overflow: "hidden", background: "#0d9488" }}
      >
        <img
          src={imageUrl}
          alt="Avatar"
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      </div>
    );
  }

  if (stream) {
    return (
      <div
        className={className}
        style={{ ...sizeStyle, borderRadius: fillContainer ? "0.5rem" : "50%", overflow: "hidden", background: "#0d9488" }}
      >
        <video
          ref={videoRef}
          autoPlay
          muted={false}
          playsInline
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      </div>
    );
  }

  if (videoUrl) {
    return (
      <div
        className={className}
        style={{ ...sizeStyle, borderRadius: fillContainer ? "0.5rem" : "50%", overflow: "hidden", background: "#0d9488" }}
      >
        <video
          src={videoUrl}
          autoPlay
          muted={false}
          playsInline
          onEnded={() => onVideoEnded?.()}
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      </div>
    );
  }

  const mouthY = isSpeaking ? 24 : 22;
  const mouthRy = isSpeaking ? 6 : 3;

  return (
    <div className={className} style={sizeStyle} title={isSpeaking ? "Speaking…" : "Avatar"}>
      <svg
        viewBox="0 0 48 48"
        className="w-full h-full block"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle cx="24" cy="24" r="22" fill="#f0fdfa" stroke="rgba(255,255,255,0.5)" strokeWidth="1.5" />
        <ellipse cx="16" cy="20" rx="3" ry="4" fill="#0d9488" />
        <ellipse cx="32" cy="20" rx="3" ry="4" fill="#0d9488" />
        <ellipse
          cx="24"
          cy={mouthY}
          rx="8"
          ry={mouthRy}
          fill="#0d9488"
          style={{
            transition: "cy 0.15s ease-out, ry 0.15s ease-out",
          }}
        />
        {isSpeaking && (
          <circle cx="24" cy="24" r="20" fill="none" stroke="rgba(13,148,136,0.4)" strokeWidth="1.5" className="animate-ping" style={{ animationDuration: "1.5s" }} />
        )}
      </svg>
    </div>
  );
}
